import React from 'react';
import { SuspectedMergeFlag } from '../../types/speakerCorrection';

export interface TimelineSegment {
  id: string;             // transcript_segments.id
  index: number;          // 1-based segment index used in suspectedMerges
  speakerId: string;
  speakerDisplayName: string;
  text: string;
  startTime: number;
  endTime: number;
}

interface Props {
  segments: TimelineSegment[];
  suspectedMerges: SuspectedMergeFlag[];
  resolvedSegmentIds: Set<string>;
  autoCorrectedSegmentIds: Set<string>;
  selectedSegmentIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onPlaySegment: (segment: TimelineSegment) => void;
}

export function SpeakerTimeline({
  segments,
  suspectedMerges,
  resolvedSegmentIds,
  autoCorrectedSegmentIds,
  selectedSegmentIds,
  onSelectionChange,
  onPlaySegment,
}: Props) {
  const flaggedIndices = new Set<number>();
  for (const m of suspectedMerges) {
    for (const idx of m.segmentIndices) flaggedIndices.add(idx);
  }
  const selected = new Set(selectedSegmentIds);

  function toggleSegment(seg: TimelineSegment, withShift: boolean) {
    if (withShift && selectedSegmentIds.length > 0) {
      // Range-select between last selected and this one
      const last = segments.find((s) => s.id === selectedSegmentIds[selectedSegmentIds.length - 1]);
      if (last) {
        const lo = Math.min(last.index, seg.index);
        const hi = Math.max(last.index, seg.index);
        const ids = segments.filter((s) => s.index >= lo && s.index <= hi).map((s) => s.id);
        onSelectionChange(Array.from(new Set([...selectedSegmentIds, ...ids])));
        return;
      }
    }
    if (selected.has(seg.id)) {
      onSelectionChange(selectedSegmentIds.filter((id) => id !== seg.id));
    } else {
      onSelectionChange([...selectedSegmentIds, seg.id]);
    }
  }

  // Group segments by speakerId in encounter order.
  const groups = new Map<string, TimelineSegment[]>();
  for (const seg of segments) {
    const arr = groups.get(seg.speakerId) ?? [];
    arr.push(seg);
    groups.set(seg.speakerId, arr);
  }

  return (
    <div className="space-y-4">
      {Array.from(groups.entries()).map(([speakerId, segs]) => (
        <div key={speakerId} className="border rounded p-3">
          <div className="text-sm font-medium mb-2">{speakerId} <span className="text-gray-500">({segs.length})</span></div>
          <div className="space-y-1">
            {segs.map((seg) => {
              const isFlagged = flaggedIndices.has(seg.index);
              const isResolved = resolvedSegmentIds.has(seg.id);
              const isSelected = selected.has(seg.id);
              return (
                <div
                  key={seg.id}
                  data-testid={`segment-${seg.index}`}
                  className={[
                    'flex items-center gap-2 p-2 text-sm rounded cursor-pointer',
                    isSelected ? 'bg-blue-100' : 'hover:bg-gray-50',
                    isFlagged && !isResolved ? 'border-l-4 border-amber-500' : '',
                    isFlagged && isResolved ? 'border-l-4 border-green-500' : '',
                  ].join(' ')}
                  onClick={(e) => toggleSegment(seg, e.shiftKey)}
                >
                  <span className="text-xs text-gray-400 w-10">{seg.startTime.toFixed(1)}s</span>
                  <span className="flex-1">{seg.text}</span>
                  {autoCorrectedSegmentIds.has(seg.id) && (
                    <span
                      className="text-xs px-1 rounded bg-slate-200"
                      title="Auto-corrected by system"
                    >
                      ⚙ System
                    </span>
                  )}
                  {isFlagged && !isResolved && (
                    <span className="text-xs px-1 rounded bg-amber-200">flag</span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onPlaySegment(seg); }}
                    aria-label={`Play segment ${seg.index}`}
                    className="text-xs text-blue-600 underline"
                  >
                    play
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
